import { axiosReadHelper } from "../../shared/service/axios-read-helper";
import { AxiosReadPayload } from "../../shared/service/axios-read-payload";
import { AxiosResultStatus } from "../../shared/service/axios-result-status";
import { axiosSaveHelper } from "../../shared/service/axios-save-helper";
import { AxiosSavePayload } from "../../shared/service/axios-save-payload";
import { AxiosDeletePayload } from "../../shared/service/axios-delete-payload";
import { axiosDeleteHelper } from "../../shared/service/axios-delete-helper";
import { getState } from "../../shared/misc/us-states";
import {
  ClientContactForm,
  ClientDeficiencyNotification,
  ClientEntityForm,
  ClientForm,
  emptyClientDeficiencyNotification,
} from "../models/client";
import { ClientStatusCardViewModel } from "../../shared/model/client-status-card";
import { client, ClientBusinessRules } from "../../shared/model/client";
import { Utils } from "../../shared/utils";
import { FacilityRiskThreshold, PayerClaimRate } from "../../admissions-advisor/models/risk-assessment";
import {
  ClientReferralSource,
  FacilityLOCReferralSource,
  FacilityLOCReferralSourceForm,
  FacilityReferralSourceModel,
  SelectedFacilityReferralSource,
} from "../models/referral-source";
import { EstLevelOfCare } from "../../admissions-advisor/models/estimator";
import {ClientPaymentTransactionFee} from '../models/payment-transaction-fee';
import { ClientRiskAssessmentPostBody } from "src/shared/model/client-risk-assessment-config";

class SpecialistClientService {
  async getClientVobs(): Promise<AxiosResultStatus> {
    const readPayload: AxiosReadPayload = {
      dataId: -2,
      url: `vob/v2/client/vob`,
    };
    return await axiosReadHelper(readPayload);
  }
  async getClients(includeFacility: boolean): Promise<AxiosResultStatus> {
    const readPayload: AxiosReadPayload = {
      dataId: -2,
      url: `clients/v2/client?includeFacility=${includeFacility}`,
    };
    const responseData = await axiosReadHelper(readPayload);

    let allClients: ClientStatusCardViewModel[] = responseData.entity;

    // Always ignore ClientId 0
    allClients = allClients.filter((client) => client.clientId !== 0);

    responseData.entity = this.filterClients(allClients);
    return responseData;
  }

  async getClientKPIs(
    clientId: number,
    months: number
  ): Promise<AxiosResultStatus> {
    const payload: AxiosReadPayload = {
      url:
        months === 1
          ? `client/${clientId}/kpi`
          : `client/${clientId}/kpi?months=${months}`,
    };
    return await axiosReadHelper(payload);
  }

  async getClient(clientId: number): Promise<AxiosResultStatus> {
    const readPayload: AxiosReadPayload = {
      dataId: -2,
      url: `clients/v2/client/${clientId}/detail`,
    };

    const responseData = await axiosReadHelper(readPayload);

    if (!responseData.hasErrors) {
      let client = responseData.entity;

      const clientEntityDetails: ClientForm =
        this.mapToClientEntityDetails(client);
      const clientBusinessRules: ClientBusinessRules =
        this.mapToClientBusinessRules(client);

      responseData.entity = {
        clientEntityDetails,
        clientBusinessRules,
        fullClient: client,
      };
    }
    return responseData;
  }

  clientMap(payload: any){
    return{
      workflowId: payload.workflowId,
      workflowStatusId: payload.workflowStatus.workflowStatusId,
      workflowSubStatusId: payload.workflowSubStatus.workflowSubStatusId
    }
  }

  async saveClient(data: {
    clientId: number,
    payload: any
  }): Promise<AxiosResultStatus>{

    const payload: AxiosSavePayload = {
      dataToSave: this.clientMap(data.payload),
      dataId: data.clientId,
      url: `clients/v2/client`
    }

   return await axiosSaveHelper(payload);

  }

  async saveClientPaymentTransactionFee(data: {
    clientId: number,
    payload: ClientPaymentTransactionFee
  }): Promise<AxiosResultStatus>{

    const payload: AxiosSavePayload = {
      dataId: 0,
      dataToSave: data.payload,
      url: `clients/v2/client/${data.clientId}/client-payment-transaction-fee`
    }

    return await axiosSaveHelper(payload)
  }

  async getClientPaymentTransactionFee(clientId: number): Promise<AxiosResultStatus>{

    const payload: AxiosReadPayload = {
      url: `clients/v2/client/${clientId}/client-payment-transaction-fee`
    }

    return await axiosReadHelper(payload)
  }

  async saveClientEntity(
    formValues: ClientEntityForm,
    originalClient: any
  ): Promise<AxiosResultStatus> {
    formValues = Utils.trimFormFields(formValues);
    const primaryAddress = {
      ...originalClient.primaryContact.primaryAddress,
      streetAddress1: formValues.streetAddress1,
      streetAddress2: formValues.streetAddress2,
      city: formValues.city,
      state: getState(formValues.state),
      zipCode: formValues.zipCode,
    };

    const primaryContact = {
      primaryAddress: primaryAddress,
    };

    const clientToSave = {
      clientName: formValues.companyName,
      websiteUrl: formValues.website,
      primaryContact,
    };

    const payload: AxiosSavePayload = {
      dataToSave: clientToSave,
      dataId: -2,
      isPatch: true,
      url: `clients/v2/client/${originalClient.clientId}`,
    };

    const response = await axiosSaveHelper(payload);

    if (!response.hasErrors) {
      const returnedClient = response.entity;
      const clientEntityDetails = this.mapToClientEntityDetails(returnedClient);

      response.entity = {
        clientEntityDetails,
        fullClient: returnedClient,
      };
    }
    return response;
  }

  async saveClientContact(
    formValues: ClientContactForm,
    originalClient: any
  ): Promise<AxiosResultStatus> {
    formValues = Utils.trimFormFields(formValues);
    const primaryContact = {
      ...originalClient.primaryContact,
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      email: formValues.email,
      primPhoneNum: formValues.phone,
      primPhoneExtn: formValues.ext,
      secPhoneNum: formValues.mobilePhone,
    };

    const clientToSave = {
      primaryContact,
    };

    const payload: AxiosSavePayload = {
      dataToSave: clientToSave,
      dataId: -2,
      isPatch: true,
      url: `clients/v2/client/${originalClient.clientId}`,
    };

    const response = await axiosSaveHelper(payload);
    if (!response.hasErrors) {
      const returnedClient: client = response.entity;
      const clientEntityDetails = this.mapToClientEntityDetails(returnedClient);

      response.entity = {
        clientEntityDetails,
        fullClient: returnedClient,
      };
    }
    return response;
  }

  async generateClientSpreadsheet(
    clientId: number,
    spreadsheetType: string,
    facilityIds: number[],
    facilityLevelOfCareIds: number[],
    payorIds: number[]
  ): Promise<AxiosResultStatus> {
    const LOCfilters = {
      clientFacilityIds: facilityIds,
      facilityLevelOfCareIds: facilityLevelOfCareIds,
    };
    const payorFilters = {
      clientFacilityIds: facilityIds,
      payorIds: payorIds,
      facilityLevelOfCareIds: facilityLevelOfCareIds
    };

    const clientToSave = {
      filters: spreadsheetType === "levelOfCare" ? LOCfilters : payorFilters,
      type: spreadsheetType,
    };

    const payload: AxiosSavePayload = {
      dataToSave: clientToSave,
      dataId: -2,
      url: `clients/v2/client/${clientId}/spreadsheet`,
    };
    const responseData = await axiosSaveHelper(payload);

    if (!responseData.hasErrors) {
      const link = document.createElement("a");
      link.href = responseData.entity.url;
      document.body.appendChild(link);
      link.click();
      link.remove();
    }

    return responseData;
  }

  async notifyClient(clientId: number): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataId: 0,
      url: `client/${clientId}/notify`,
    };
    const responseData = await axiosSaveHelper(payload);
    if (!responseData.hasErrors) {
      let client = responseData.entity;
      responseData.entity = {
        updatedClient: client,
      };
    }
    return responseData;
  }

  async resendInvitation(userId: number): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      url: `user/v2/user/${userId}/welcome-email`,
    };
    return await axiosSaveHelper(payload);
  }

  async saveClientBusinessRules(
    formValues: ClientBusinessRules
  ): Promise<AxiosResultStatus> {
    const clientId = formValues.clientId;

    const businessRulesToSave = {
      clientBusRules: {
        ...formValues,
        clientId: formValues.clientId,
      },
    };

    const payload: AxiosSavePayload = {
      dataToSave: businessRulesToSave,
      dataId: -2,
      isPatch: true,
      url: `clients/v2/client/${clientId}/detail`,
    };
    const response = await axiosSaveHelper(payload);

    if (!response.hasErrors) {
      const client: client = response.entity;
      response.entity = {
        clientBusinessRules: client.clientBusRules,
        fullClient: client,
      };
    }
    return response;
  }

  async createFacilityRiskSetting(
    formValues: FacilityRiskThreshold
  ): Promise<AxiosResultStatus> {
    let newRiskThresholdPayload: any = Utils.deepClone(formValues);
    delete newRiskThresholdPayload.paramId;
    delete newRiskThresholdPayload.facilityRiskThresholdId;
    const payload: AxiosSavePayload = {
      dataToSave: newRiskThresholdPayload,
      dataId: formValues.paramId,
      url: `clients/v2/facility/${formValues.clientFacilityId}/risk-threshold`,
    };
    const result = await axiosSaveHelper(payload);
    return result;
  }

  async updatePayerClaimRate(formValues: PayerClaimRate): Promise<AxiosResultStatus> {
    let newRiskThresholdPayload: any = Utils.deepClone(formValues);
    delete newRiskThresholdPayload.paramId;
    delete newRiskThresholdPayload.clientPayorClaimrateId;
    delete newRiskThresholdPayload.clientId;
    if (newRiskThresholdPayload.cfgPayorPlanId === null) {
      delete newRiskThresholdPayload.cfgPayorPlanId;
    }
    if (newRiskThresholdPayload.cfgPayorId === null) {
      delete newRiskThresholdPayload.cfgPayorId;
    }
    const payload: AxiosSavePayload = {
      dataToSave: newRiskThresholdPayload,
      dataId: formValues.paramId,
      isPatch: true,
      url: `clients/v2/client/payor-claimrate/${formValues.clientPayorClaimrateId}`
    }
    const result = await axiosSaveHelper(payload);
    return result;
  }

  async createPayerClaimRate(formValues: PayerClaimRate): Promise<AxiosResultStatus> {
    let newRiskThresholdPayload: any = Utils.deepClone(formValues);
    delete newRiskThresholdPayload.paramId;
    delete newRiskThresholdPayload.clientPayorClaimrateId;
    if (newRiskThresholdPayload.cfgPayorPlanId === null) {
      delete newRiskThresholdPayload.cfgPayorPlanId;
    }
    if (newRiskThresholdPayload.cfgPayorPlanId === null) {
      delete newRiskThresholdPayload.cfgPayorPlanId;
    }
    if (newRiskThresholdPayload.cfgPayorId === null) {
      delete newRiskThresholdPayload.cfgPayorId;
    }
    const payload: AxiosSavePayload = {
      dataToSave: newRiskThresholdPayload,
      dataId: formValues.paramId,
      url: `clients/v2/client/${formValues.clientId}/payor-claimrate`
    }
    const result = await axiosSaveHelper(payload);
    return result;
  }

  async updateFacilityRiskSetting(formValues: FacilityRiskThreshold): Promise<AxiosResultStatus> {
    let newRiskThresholdPayload: any = Utils.deepClone(formValues);
    delete newRiskThresholdPayload.paramId;
    delete newRiskThresholdPayload.facilityRiskThresholdId;
    delete newRiskThresholdPayload.clientFacilityId;
    const payload: AxiosSavePayload = {
      dataToSave: newRiskThresholdPayload,
      dataId: formValues.paramId,
      isPatch: true,
      url: `clients/v2/facility/risk-threshold/${formValues.facilityRiskThresholdId}`,
    };
    const result = await axiosSaveHelper(payload);
    return result;
  }

  isNotFilterByAll(payload: any): boolean {
    const payloadCopy = { ...payload };
    delete payloadCopy.all;
    const payloadValues = Object.values(payloadCopy);
    return payloadValues.some((value) => value === true);
  }

  async getReferralSources(
    facilityId: number,
    clientId: number
  ): Promise<AxiosResultStatus> {
    let referralSourceName = "clientReferralSourceName";
    let endPoint = "clients/v2/client/" + clientId + "/referral-source";
    if (facilityId > 0) {
      endPoint = "clients/v2/facility/" + facilityId + "/referral-source";
      referralSourceName = "facilityReferralSourceName";
    }

    const readPayload: AxiosReadPayload = {
      dataId: -2,
      url: endPoint,
    };

    const responseData = await axiosReadHelper(readPayload);

    if (!responseData.hasErrors) {
      const newSortedData = Utils.sortAlphabetically(
        responseData.entity,
        referralSourceName
      );

      if (facilityId > 0) {
        const mappedData = this.mapToSelectedFacilityReferralSource(
          newSortedData,
          clientId
        );
        responseData.entity = mappedData;
      } else {
        responseData.entity = newSortedData;
      }
    }
    return responseData;
  }

  async getFacilityLOCReferralSources(
    facilityId: number,
    clientId: number,
    facilityReferralSources: SelectedFacilityReferralSource[],
    facilityLevelsOfCare: EstLevelOfCare[],
    levelOfCareId: number
  ): Promise<AxiosResultStatus> {
    let endpoint = "clients/v2/facility/" + facilityId + "/referral-source-loc";

    const readPayload: AxiosReadPayload = {
      dataId: -2,
      url: endpoint,
    };

    const responseData = await axiosReadHelper(readPayload);

    if (!responseData.hasErrors) {
      //manipulate response with facilityReferralSources to add the property facilityReferralSourceName and levelOfCareRevenue for each row
      let facilityLOCRefSources = responseData.entity;

      const facilityLOCReferralSourceForms: FacilityLOCReferralSourceForm[] =
        this.mapToFacilityLOCReferralSourceForm(
          facilityLOCRefSources,
          clientId,
          facilityLevelsOfCare,
          facilityReferralSources,
          levelOfCareId
        );
      responseData.entity = {
        facilityLOCReferralSourceForms,
        apiFacilityLOCReferralSources: facilityLOCRefSources,
      };
    }

    return responseData;
  }

  async getClientDeficiencyNotification(
    clientId: number
  ): Promise<AxiosResultStatus> {
    const endpoint = `clients/v2/client/${clientId}/deficiency-notification`;

    const readPayload: AxiosReadPayload = {
      dataId: -2,
      url: endpoint,
    };

    const responseData = await axiosReadHelper(readPayload);

    if (!responseData.hasErrors) {
      const newValue: ClientDeficiencyNotification =
        this.mapToClientDeficiencyNotification(clientId, responseData.entity);
      responseData.entity = newValue;
    }

    return responseData;
  }


  // private supporting functions

  filterClients(allClients: ClientStatusCardViewModel[]) {
    // workflow statuses, ids, etc: https://anexinet.atlassian.net/wiki/spaces/FNP/pages/2716730103/Master+Workflow+Database+List
    return {
      allClients: allClients,
      setupIncomplete: allClients.filter(
        (client) => client.workflowSubStatus.workflowSubStatusId === 1
      ),
      stripeSetupIncomplete: allClients.filter(
        (client) => client.workflowSubStatus.workflowSubStatusId === 2
      ),
      readyForActivation: allClients.filter(
        (client) => client.workflowSubStatus.workflowSubStatusId === 3
      ),
      isActive: allClients.filter(
        (client) => client.workflowSubStatus.workflowSubStatusId === 4
      ),
      stripeActionNeeded: allClients.filter(
        (client) => client.workflowSubStatus.workflowSubStatusId === 5
      ),
      closed: allClients.filter(
        (client) => client.workflowSubStatus.workflowSubStatusId === 6
      ),
    };
  }

  mapToClientEntityDetails(client: client): ClientForm {
    return {
      clientId: client.clientId ? client.clientId : 0,
      companyName: client.clientName ? client.clientName : "",
      website: client.websiteUrl ? client.websiteUrl : "",
      streetAddress1: client.primaryContact?.primaryAddress?.streetAddress1
        ? client.primaryContact.primaryAddress.streetAddress1
        : "",
      streetAddress2: client.primaryContact?.primaryAddress?.streetAddress2
        ? client.primaryContact.primaryAddress.streetAddress2
        : "",
      city: client.primaryContact?.primaryAddress?.city
        ? client.primaryContact.primaryAddress.city
        : "",
      state: client.primaryContact?.primaryAddress?.state?.stateId
        ? client.primaryContact.primaryAddress.state.stateId
        : 0,
      zipCode: client.primaryContact?.primaryAddress?.zipCode
        ? client.primaryContact.primaryAddress.zipCode
        : "",
      firstName: client.primaryContact?.firstName
        ? client.primaryContact.firstName
        : "",
      lastName: client.primaryContact?.lastName
        ? client.primaryContact.lastName
        : "",
      email: client.primaryContact?.email ? client.primaryContact.email : "",
      phone: client.primaryContact?.primPhoneNum
        ? client.primaryContact.primPhoneNum
        : "",
      ext: client.primaryContact?.primPhoneExtn
        ? client.primaryContact.primPhoneExtn
        : "",
      mobilePhone: client.primaryContact?.secPhoneNum
        ? client.primaryContact.secPhoneNum
        : "",
    };
  }

  mapToClientBusinessRules(client: client): ClientBusinessRules {
    return {
      clientId: client.clientBusRules?.clientId || 0,
      preCareFullPayMaxDiscount:
        client.clientBusRules?.preCareFullPayMaxDiscount === 0
          ? 0
          : client.clientBusRules?.preCareFullPayMaxDiscount || "",
      postCareFullPayMaxDiscount:
        client.clientBusRules?.postCareFullPayMaxDiscount === 0
          ? 0
          : client.clientBusRules?.postCareFullPayMaxDiscount || "",
      clientBusinessRuleId: client.clientBusRules?.clientBusinessRuleId || 0,
      refundsIssuedBy: client.clientBusRules?.refundsIssuedBy || "",
      missedPmtsForDefault: client.clientBusRules?.missedPmtsForDefault || "",
      pfrVarianceTolerance:
        client.clientBusRules?.pfrVarianceTolerance === 0
          ? 0
          : client.clientBusRules?.pfrVarianceTolerance || "",
      automationFee:
        client.clientBusRules?.automationFee === 0
          ? 0
          : client.clientBusRules?.automationFee || "",
      merchantProcessingFee:
        client.clientBusRules?.merchantProcessingFee === 0
          ? 0
          : client.clientBusRules?.merchantProcessingFee || "",
      customDocTokenId: client.clientBusRules?.customDocTokenId || "",
      useClientTemplate: client.clientBusRules?.useClientTemplate || "All",
      realTimeUpdates: client.clientBusRules?.realTimeUpdates || false,
      paynowFee:
        client.clientBusRules?.paynowFee === 0
          ? 0
          : client.clientBusRules?.paynowFee || "",
      sendPFRDocs: client.clientBusRules?.sendPFRDocs || false,
    };
  }

  mapToSelectedFacilityReferralSource(
    facilityRefSources: FacilityReferralSourceModel[],
    clientId: number
  ): SelectedFacilityReferralSource[] {
    let newSelectedFacilityRefSources: SelectedFacilityReferralSource[] = [];
    facilityRefSources.forEach((refItem: FacilityReferralSourceModel) => {
      const newRefItem: SelectedFacilityReferralSource = {
        facilityId: refItem.clientFacilityId,
        clientReferralSourceId: refItem.clientReferralSourceId,
        facilityReferralSourceId: refItem.facilityReferralSourceId,
        clientId: clientId,
        facilityReferralSourceName: refItem.facilityReferralSourceName,
        clientReferralSourceName:
          refItem.clientReferralSourceId === undefined ||
          refItem.clientReferralSourceId === null
            ? ""
            : refItem.facilityReferralSourceName,
      };
      newSelectedFacilityRefSources.push(newRefItem);
    });
    return newSelectedFacilityRefSources;
  }

  mapToFacilityLOCReferralSourceForm(
    facilityLOCRefSources: FacilityLOCReferralSource[],
    clientId: number,
    facilityLevelsOfCare: EstLevelOfCare[],
    facilityReferralSources: SelectedFacilityReferralSource[],
    levelOfCareId: number
  ): FacilityLOCReferralSourceForm[] {
    let newFacilityLOCRefSources: FacilityLOCReferralSourceForm[] = [];
    facilityReferralSources.forEach(
      (refItem: SelectedFacilityReferralSource) => {
        const facilityRefSourceId = refItem.facilityReferralSourceId;
        let newFacilityLOCReferralForm: FacilityLOCReferralSourceForm = {
          clientId: clientId,
          facilityReferralSourceId: facilityRefSourceId,
          facilityReferralSourceName: refItem.facilityReferralSourceName,
          clientFacilityId: refItem.facilityId,
          facilityLocReferralSourceId: undefined,
          facilityLevelOfCareId: levelOfCareId,
          locReferralRevenue: 0,
          levelOfCareName: Utils.getLevelOfCareName(
            facilityLevelsOfCare,
            levelOfCareId
          ),
          levelOfCareCode: Utils.getLevelOfCareCode(
            facilityLevelsOfCare,
            levelOfCareId
          ),
        };
        let facilityLOCRefSourceItem = facilityLOCRefSources.find(
          (item: FacilityLOCReferralSource) =>
            item.facilityReferralSourceId === facilityRefSourceId &&
            item.facilityLevelOfCareId === levelOfCareId
        );
        if (facilityLOCRefSourceItem !== undefined) {
          newFacilityLOCReferralForm.facilityLocReferralSourceId =
            facilityLOCRefSourceItem.facilityLocReferralSourceId;
          newFacilityLOCReferralForm.locReferralRevenue =
            facilityLOCRefSourceItem.locReferralRevenue;
        }
        newFacilityLOCRefSources.push(newFacilityLOCReferralForm);
      }
    );

    return newFacilityLOCRefSources;
  }

  mapToClientDeficiencyNotification(
    clientId: number,
    responseEntity: ClientDeficiencyNotification
  ): ClientDeficiencyNotification {
    let retDeficiencyNotification: ClientDeficiencyNotification;
    if (responseEntity === null) {
      //return empty object with valid ClientId
      retDeficiencyNotification = { ...emptyClientDeficiencyNotification };
      retDeficiencyNotification.clientId = clientId;
    } else {
      retDeficiencyNotification = { ...responseEntity };
    }
    return retDeficiencyNotification;
  }

  async getClientRiskAssessmentConfigs(
    clientId: number,
    facilityId?: number
  ): Promise<AxiosResultStatus> {
    let endpoint = `clients/v2/client/${clientId}/risk-assessment-config`;

    const readPayload: AxiosReadPayload = {
      dataId: -2,
      url: endpoint,
    };

    const responseData = await axiosReadHelper(readPayload);

    return responseData;
  }


  async uploadRateFileToS3(
      filePath: string,
      bucketName:string,
      file: any
  ): Promise<AxiosResultStatus> {
    let data = new FormData();
    data.append("file", file);
    data.append("bucketName",bucketName);
    data.append("filePath",filePath);

    const payload: AxiosSavePayload = {
      dataToSave: data,
      dataId: 0,
      url: 'configuration/v2/s3file',
    };

    return await axiosSaveHelper(payload);
  }

}

export const specialistClientService = new SpecialistClientService();
